import { usePage } from "@inertiajs/react"
import { useEffect, useRef } from "react"

import type { SharedData } from "@/types"

const HELP_SCOUT_BEACON_ID = "f3868e85-69f4-4c6c-8035-a167e6108a94"
const HELP_SCOUT_BEACON_SCRIPT_ID = "help-scout-beacon-script"
const HELP_SCOUT_BEACON_SRC = "https://beacon-v2.helpscout.net"

interface BeaconQueueItem {
  method: string
  options?: unknown
  data?: unknown
}

interface BeaconCommand {
  (method: string, options?: unknown, data?: unknown): void
  readyQueue?: BeaconQueueItem[]
}

const createBeaconStub = (): BeaconCommand => {
  const stub = ((method: string, options?: unknown, data?: unknown) => {
    stub.readyQueue?.push({ method, options, data })
  }) as BeaconCommand

  stub.readyQueue = []

  return stub
}

const ensureBeaconScript = () => {
  if (typeof window === "undefined") {
    return
  }

  window.Beacon ??= createBeaconStub()

  if (document.getElementById(HELP_SCOUT_BEACON_SCRIPT_ID)) {
    return
  }

  const script = document.createElement("script")
  script.id = HELP_SCOUT_BEACON_SCRIPT_ID
  script.async = true
  script.src = HELP_SCOUT_BEACON_SRC
  document.body.appendChild(script)
}

export default function HelpScoutBeacon() {
  const page = usePage<SharedData>()
  const isInitializedRef = useRef(false)
  const isDashboardRoute = page.url.startsWith("/dashboard")
  const user = page.props.auth?.user

  useEffect(() => {
    if (!isDashboardRoute) {
      if (isInitializedRef.current) {
        window.Beacon?.("destroy")
        isInitializedRef.current = false
      }

      return
    }

    ensureBeaconScript()

    if (!isInitializedRef.current) {
      window.Beacon?.("init", HELP_SCOUT_BEACON_ID)
      isInitializedRef.current = true
    }

    window.Beacon?.("config", {
      display: {
        zIndex: 40,
      },
      hideFABOnMobile: true,
    })

    if (user?.email) {
      window.Beacon?.("identify", {
        name: user.name,
        email: user.email,
      })
    }
  }, [isDashboardRoute, user?.email, user?.name])

  return isDashboardRoute ? (
    <style>
      {`
        iframe[src*="beacon-v2.helpscout.net"],
        iframe[src*="helpscout.net"],
        #BeaconContainer,
        [data-beacon-container] {
          z-index: 40 !important;
        }

        @media (max-width: 1023px) {
          iframe[src*="beacon-v2.helpscout.net"],
          iframe[src*="helpscout.net"],
          #BeaconContainer,
          [data-beacon-container] {
            bottom: calc(env(safe-area-inset-bottom, 0px) + 5.5rem) !important;
          }
        }
      `}
    </style>
  ) : null
}
